import { useLanguage } from "@/contexts/LanguageContext";

interface PageTopBannerProps {
  title: string;
  subtitle?: string;
  /** Optional translation keys; when provided, override title/subtitle. */
  titleKey?: string;
  subtitleKey?: string;
}

export const PageTopBanner = ({ title, subtitle, titleKey, subtitleKey }: PageTopBannerProps) => {
  const { t } = useLanguage();
  const displayTitle = titleKey ? t(titleKey) : title;
  const displaySubtitle = subtitleKey ? t(subtitleKey) : subtitle;

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-blob/20 via-background to-blob/10 border-b border-blob/30">
      {/* Animated background blobs */}
      <div className="absolute inset-0 overflow-hidden opacity-40">
        <div className="absolute -top-10 -left-10 w-72 h-72 bg-gradient-to-br from-blob/50 to-transparent rounded-full blur-3xl animate-blob" />
        <div className="absolute top-1/4 -right-20 w-96 h-96 bg-gradient-to-bl from-blob/40 to-transparent rounded-full blur-3xl animate-blob animation-delay-2000" />
        <div className="absolute -bottom-10 left-1/3 w-64 h-64 bg-gradient-to-tr from-blob/30 to-transparent rounded-full blur-3xl animate-blob animation-delay-4000" />
      </div>

      {/* Content */}
      <div className="relative max-w-6xl mx-auto px-6 py-16 md:py-20">
        <div className="text-center space-y-4">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold bg-gradient-to-r from-foreground via-blob to-foreground bg-clip-text text-transparent animate-fade-in">
            {displayTitle}
          </h1>
          {displaySubtitle && (
            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto animate-fade-in-slow">
              {displaySubtitle}
            </p>
          )}
          <div className="flex justify-center gap-2 pt-4 animate-fade-in-slow">
            <div className="w-20 h-1 bg-gradient-to-r from-transparent via-blob to-transparent rounded-full" />
            <div className="w-12 h-1 bg-gradient-to-r from-transparent via-blob/60 to-transparent rounded-full" />
            <div className="w-8 h-1 bg-gradient-to-r from-transparent via-blob/40 to-transparent rounded-full" />
          </div>
        </div>
      </div>

      {/* Bottom wave decoration */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-background to-transparent" />
    </div>
  );
};

